import { z } from "zod";
import { nullishToOptional } from "../helpers";
import { GitProvider } from "./git-providers";
import { PulsarContributionConfigurationProperty, PulsarContributionVariant, PulsarCustomBlock } from "./pulsar";
import { ExporterPropertyDefinition } from "./export-configuration";

export const ExporterType = z.enum(["code", "documentation"]);
export const ExporterSource = z.enum(["git", "upload"]);
export const ExporterTag = z.string();

export const ExporterPulsarDetails = z.object({
  description: z.string(),
  version: z.string(),
  routingVersion: nullishToOptional(z.string()),
  author: nullishToOptional(z.string()),
  organization: nullishToOptional(z.string()),
  homepage: nullishToOptional(z.string()),
  readme: nullishToOptional(z.string()),
  tags: nullishToOptional(z.array(ExporterTag)).default([]),
  packageId: nullishToOptional(z.string().max(255)),

  iconURL: nullishToOptional(z.string()),
  configurationProperties: nullishToOptional(z.array(PulsarContributionConfigurationProperty)).default([]),
  customBlocks: nullishToOptional(z.array(PulsarCustomBlock)).default([]),
  blockVariants: nullishToOptional(z.record(z.string(), z.array(PulsarContributionVariant))).default({}),
  properties: nullishToOptional(ExporterPropertyDefinition.array()),

  usesBrands: nullishToOptional(z.boolean()).default(false),
  usesThemes: nullishToOptional(z.boolean()).default(false),
  usesLocale: nullishToOptional(z.boolean()).default(false),
});

export const ExporterDetails = ExporterPulsarDetails.extend({
  source: ExporterSource,

  gitProvider: nullishToOptional(GitProvider),
  gitUrl: nullishToOptional(z.string()),
  gitBranch: nullishToOptional(z.string()),
  gitDirectory: nullishToOptional(z.string()),
});

export const Exporter = z.object({
  id: z.string(),
  createdAt: z.coerce.date(),
  name: z.string(),
  isPrivate: z.boolean(),
  details: ExporterDetails,
  exporterType: nullishToOptional(ExporterType).default("code"),
  storagePath: nullishToOptional(z.string()).default(""),
  properties: nullishToOptional(ExporterPropertyDefinition.array()),
  isDeprecated: z.boolean().default(false),
  deprecationNote: nullishToOptional(z.string()),
  replacementExporterId: nullishToOptional(z.string()),
});

export type ExporterType = z.infer<typeof ExporterType>;
export type ExporterSource = z.infer<typeof ExporterSource>;
export type ExporterTag = z.infer<typeof ExporterTag>;
export type ExporterPulsarDetails = z.infer<typeof ExporterPulsarDetails>;
export type ExporterDetails = z.infer<typeof ExporterDetails>;
export type Exporter = z.infer<typeof Exporter>;
